/**
 * Next fetch options for catalogue requests.
 *
 * Next keys its fetch cache on the URL and on the request headers, including
 * `Authorization`. Every anonymous visitor shares one bearer token, so their
 * responses share cache entries (per base store and language, which are part of
 * the URL). A signed-in shopper's request carries their own OCC token, so their
 * entries are theirs alone: the shopper's promotion-engine pricing is cached
 * for them without ever reaching another visitor.
 *
 * How long an entry lives is set per environment, in seconds:
 * - `D2C_PRODUCT_API_CACHE_SECONDS_ANONYMOUS`: anonymous visitors (search, detail, articles)
 * - `D2C_PRODUCT_API_CACHE_SECONDS_SHOPPER`: signed-in shoppers
 * - `D2C_PRODUCT_COUNT_CACHE_SECONDS`: the product count of a listing
 *
 * They apply in development too. `0` switches that cache off; a missing or
 * invalid value falls back to the default.
 */
export interface ProductFetchOptions {
  cache?: RequestCache;
  next?: { revalidate: number; tags: string[] };
}

export const CATALOGUE_CACHE_ENV = {
  anonymous: "D2C_PRODUCT_API_CACHE_SECONDS_ANONYMOUS",
  count: "D2C_PRODUCT_COUNT_CACHE_SECONDS",
  shopper: "D2C_PRODUCT_API_CACHE_SECONDS_SHOPPER",
} as const;

export const DEFAULT_ANONYMOUS_CACHE_SECONDS = 300;
// A shopper's own entries only help while they browse the same listing again,
// so they live shorter than the shared ones.
export const DEFAULT_SHOPPER_CACHE_SECONDS = 60;
export const DEFAULT_COUNT_CACHE_SECONDS = 300;

function readSeconds(name: string, fallback: number): number {
  const raw = process.env[name]?.trim();
  if (!raw) {
    return fallback;
  }

  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0
    ? Math.floor(seconds)
    : fallback;
}

/** How long a listing's product count stays cached; `0` means not at all. */
export function getCountCacheSeconds(): number {
  return readSeconds(CATALOGUE_CACHE_ENV.count, DEFAULT_COUNT_CACHE_SECONDS);
}

export function getProductFetchOptions(params: {
  isPersonalised: boolean;
  tags: string[];
}): ProductFetchOptions {
  const seconds = params.isPersonalised
    ? readSeconds(CATALOGUE_CACHE_ENV.shopper, DEFAULT_SHOPPER_CACHE_SECONDS)
    : readSeconds(
        CATALOGUE_CACHE_ENV.anonymous,
        DEFAULT_ANONYMOUS_CACHE_SECONDS
      );

  if (seconds === 0) {
    return { cache: "no-store" };
  }

  return { next: { revalidate: seconds, tags: params.tags } };
}
